import React, { useEffect } from 'react';
import TextInput from '../../common/TextInput';
import Select from '../../common/Select/Select';
import { IProductList } from '../../../data/models/productList';
import { FormatType } from '../../../data/models/formatType';
import { Modal } from '../../common/Modal';
import { useCreateProduct } from '../../../hooks/useCreateProduct';
import ContentAlert from '../../common/ErrorAlert/ContentAlert';
import './CreateProduct.scss';
import { translatePhysicalProductType } from '@/helpers/translateProductType';

interface Props {
  toggle: boolean;
  togglePopUp: any;
  albumToCopy?: IProductList;
  formatType?: FormatType;
}

const formatIcons: Record<string, string> = {
  vinyl: 'fa-solid fa-album',
  cassette: 'fa-solid fa-cassette-tape',
  apparel: 'fa-tshirt',
  print: 'fa-newspaper',
  'hard goods': 'fa-mug-saucer',
  accessories: 'fa-bag-shopping',
  cd: 'fa-regular fa-compact-disc',
  dvd: 'fa-film',
  'blu-ray': 'fa-film',
  sacd: 'fa-regular fa-compact-disc',
};

const getFormatIcon = (label: string) => {
  const lowerLabel = label.toLowerCase();
  const key = Object.keys(formatIcons)
    .sort((a, b) => b.length - a.length)
    .find((k) => lowerLabel.includes(k));
  return key ? formatIcons[key] : 'fa-compact-disc';
};

const CreateProduct = ({
  togglePopUp: togglePopUp,
  albumToCopy,
  formatType,
}: Props) => {
  const {
    t,
    album,
    showPopUp,
    isSaveDisabled,
    isSubmitting,
    submitError,
    formats,
    handleInput,
    handleFormat,
    handleLabelSelect,
    loadOptions,
    handleSubmit,
    closePopUp,
    loadMerchandiseTypes,
    handleMerchandiseType,
  } = useCreateProduct(togglePopUp, albumToCopy, formatType);

  useEffect(() => {
    if (formats && formats.length > 0 && !album?.format?.id) {
      handleFormat(formats[0]);
    }
  }, [formats]);

  const toCamelCase = (str: string | null) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase())
      .replace(/[^a-zA-Z0-9]/g, ''); // Final sweep to remove any trailing non-alphanumeric chars
  };

  const selectedFormatIndex = formats?.findIndex(
    (f: any) => f.value.id === album.format?.id
  );

  return (
    <Modal
      dialogClassName={`modal-lg create-product-modal ${formatType === FormatType.Merchandise ? 'merchandise' : 'physical'}`}
      titleText={
        !albumToCopy
          ? t('product.createProduct')
          : t('product.productList.duplicateProduct')
      }
      submitButtonText={
        albumToCopy
          ? t('product.productList.duplicateProduct')
          : t('common.button.createProduct')
      }
      show={showPopUp}
      submitButtonDisabled={isSaveDisabled || !album?.format || isSubmitting}
      onClose={closePopUp}
      onSubmit={handleSubmit}
    >
      {submitError ? (
        <ContentAlert contentText={submitError} dismissable={false} />
      ) : null}
      <div className="form-group">
        <TextInput
          labelClassName="modal-field-label upper-case"
          labelTitle={t('product.createProduct.title')}
          handleInput={(e) => {
            handleInput('title', e.target.value);
          }}
          id="title"
          type="text"
          value={album?.title}
          inputClassname="form-control"
          className=""
          autoFocus={true}
        ></TextInput>
      </div>
      {!albumToCopy ? (
        <div className="form-group">
          <Select
            labelTitle={t('product.createProduct.label')}
            labelClass="modal-field-label upper-case"
            id="label"
            isClearable
            loadOptions={loadOptions}
            placeholder=""
            onChange={handleLabelSelect}
            data-testid="label-select"
          ></Select>
        </div>
      ) : null}
      <div className="form-group">
        <div className="modal-field-label upper-case">
          <label htmlFor="format">{t('product.createProduct.format')}</label>
        </div>
        <div className="product-btn-group">
          {formats?.map((option: any, index: number, arr: any[]) => {
            const isSelected = album.format?.id === option.value.id;
            const isDisabled =
              albumToCopy &&
              formatType === FormatType.Merchandise &&
              !isSelected;
            const iconClass = getFormatIcon(option.label);

            return (
              <React.Fragment key={option.value.id}>
                <button
                  type="button"
                  className={`btn product-btn ${isSelected ? 'active' : ''}`}
                  disabled={isDisabled}
                  onClick={() => handleFormat(option)}
                >
                  <i className={`fas ${iconClass}`}></i>
                  <span>{translatePhysicalProductType(option.label)}</span>
                </button>
                {((index + 1) % 4 === 0 || index === arr.length - 1) && (
                  <React.Fragment>
                    <div className="spacer-rectangle-horizontal-small"></div>
                    <div className="modal-spacer-rectangle-container">
                      {selectedFormatIndex > -1 &&
                        selectedFormatIndex >= index - (index % 4) &&
                        selectedFormatIndex <= index && (
                          <div
                            className="modal-spacer-rectangle-highlight"
                            style={{
                              marginLeft: `${(selectedFormatIndex % 4) * 116}px`,
                              width: '106px',
                            }}
                          ></div>
                        )}
                    </div>
                  </React.Fragment>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {album.format && (
          <div className="product-type-info">
            <div>
              <p>
                {t(
                  `product.createProduct.selectedFormatInfo.${toCamelCase(album?.format?.name)}`
                )}
              </p>
            </div>
          </div>
        )}
      </div>

      {formatType === FormatType.Merchandise && album.format && (
        <div className="form-group">
          <Select
            key={album.format.id}
            labelTitle={t('product.editProduct.subType')}
            labelClass="modal-field-label upper-case"
            id="merchandiseType"
            isClearable
            loadOptions={(inputValue, callback) =>
              loadMerchandiseTypes(inputValue, callback, album.format?.id)
            }
            placeholder=""
            onChange={handleMerchandiseType}
            data-testid="merchandise-type-select"
          />
        </div>
      )}
    </Modal>
  );
};
export default CreateProduct;
